import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type {
  ParticipatingUpaAuctionsOfAuctionTileParams,
  ParticipatingUpaAuctionsResponse,
} from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { isServerError, useAxios, type Successful } from 'src/hooks/use-axios';

type HookOptions = {
  root: Successful<OptionalTransportAuctionServiceRootRequest>;
  tileId: Uuid;
  params: ParticipatingUpaAuctionsOfAuctionTileParams;
};

type ExecuteOptions = {
  url: string;
  params: ParticipatingUpaAuctionsOfAuctionTileParams;
};

export function useParticipatingUpaAuctionsOfAuctionTile({
  root,
  tileId,
  params,
}: HookOptions) {
  const auctions = useAxios(
    (axios, baseConfig, { url, params }: ExecuteOptions) =>
      axios.request<ParticipatingUpaAuctionsResponse>({
        ...baseConfig,
        url,
        params,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isServerError(error, 404)) {
          // either unknown or too old
          return error;
        }
        throw error;
      },
    }
  );

  const auctionsLink = root.response.data._links.participatingUpaAuctions;

  useEffect(() => {
    if (!auctionsLink) return;

    const url = urlTemplate.parse(auctionsLink.href).expand({ tileId });
    auctions.execute({ url, params });
    // oxlint-disable-next-line exhaustive-deps
  }, [auctionsLink, tileId, params]);

  if (!auctionsLink) return;

  return auctions;
}

export type ParticipatingUpaAuctionsOfAuctionTileRequest = ReturnType<
  typeof useParticipatingUpaAuctionsOfAuctionTile
>;
